<!--
  KAN-31: this campaign's own call history -- every call it originally
  answered, PLUS every call redirected TO it from elsewhere (the whole
  point of KAN-31's fix: a redirected call now shows up under both the
  default campaign AND wherever it was handed off to, not just one or the
  other). Reuses the exact same CallHistoryList/CallIntelligenceDrawer
  components and useCallHistoryData composable the global Call History page
  uses -- only difference is a fixed task_id scoping every fetch, instead of
  org-wide.
-->
<template>
  <div class="cch-root">
    <CallIntelligenceDrawer
      v-model="showIntelligenceDrawer"
      :call="selectedCall"
      :organization="organization"
    />

    <div class="cch-toolbar">
      <span class="cch-toolbar__count">{{ totalCalls }} call{{ totalCalls === 1 ? '' : 's' }}</span>
      <v-btn
        variant="outlined"
        size="small"
        class="text-none"
        prepend-icon="mdi-refresh"
        :loading="loading"
        :disabled="!isSessionReady"
        @click="handleRefresh"
      >
        Refresh
      </v-btn>
    </div>

    <CallHistoryList
      :calls="paginatedCalls"
      :loading="loading"
      :is-search-active="isSearchActive"
      @open-detail="openCallDetail"
    />

    <PortalListPagination
      class="cch-footer"
      :page="page"
      :page-count="pageCount"
      :items-per-page="itemsPerPage"
      :range-label="rangeLabel"
      :total="filteredCalls.length"
      @update:page="setPage"
      @update:items-per-page="setItemsPerPage"
    />
  </div>
</template>

<script setup>
import { ref, computed, toRef } from 'vue'
import { useStore } from 'vuex'
import PortalListPagination from '@/components/Extras/PortalListPagination.vue'
import CallIntelligenceDrawer from '@/components/CallHistory/CallIntelligenceDrawer.vue'
import CallHistoryList from '@/components/CallHistory/CallHistoryList.vue'
import {
  useCallHistoryData,
  getCallHistoryErrorMessage,
} from '@/composables/callHistory/useCallHistoryData'

defineOptions({ name: 'CampaignCallHistoryPanel' })

const props = defineProps({
  campaignId: { type: String, default: '' },
})

const emit = defineEmits(['error'])

const store = useStore()
const organization = computed(() => store.state.organization)
const showIntelligenceDrawer = ref(false)
const selectedCall = ref(null)

const {
  loading,
  page,
  itemsPerPage,
  pageCount,
  isSessionReady,
  filteredCalls,
  paginatedCalls,
  totalCalls,
  rangeLabel,
  isSearchActive,
  loadCallHistory,
  setPage,
  setItemsPerPage,
} = useCallHistoryData({ taskId: toRef(props, 'campaignId') })

async function handleRefresh() {
  try {
    const result = await loadCallHistory()
    if (result && result.status === 'ERROR') {
      emit('error', getCallHistoryErrorMessage(result))
    }
  } catch (err) {
    emit('error', getCallHistoryErrorMessage(err))
  }
}

function openCallDetail(call) {
  if (!call) return
  selectedCall.value = { ...call }
  showIntelligenceDrawer.value = true
}
</script>

<style scoped>
.cch-root {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cch-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.cch-toolbar__count {
  font-weight: 650;
  font-size: 0.9rem;
  opacity: 0.75;
}
</style>
